import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertCircle, ArrowRight, Check, Loader2, Archive, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StalledGoalsCard({ token, user, stalledGoals = [], onEngage, onCommitted }) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [showStarterForm, setShowStarterForm] = useState(false);

  const [activeIndex, setActiveIndex] = useState(0);

  const visibleGoals = stalledGoals.slice(0, 3);

  useEffect(() => {
    if (visibleGoals.length > 0 && activeIndex >= visibleGoals.length) {
      setActiveIndex(Math.max(0, visibleGoals.length - 1));
    }
  }, [visibleGoals.length, activeIndex]);

  if (visibleGoals.length === 0) return null;

  const currentStall = visibleGoals[activeIndex] || visibleGoals[0];
  const { want, stalled_days } = currentStall;
  const isStarter = user?.subscription?.tier === 'starter';

  const handleNext = () => {
    setActiveIndex((prev) => (prev < visibleGoals.length - 1 ? prev + 1 : 0));
    setShowStarterForm(false);
    setNewTodoTitle('');
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : visibleGoals.length - 1));
    setShowStarterForm(false);
    setNewTodoTitle('');
  };

  const handleRecommit = () => {
    if (isStarter) {
      setShowStarterForm(true);
    } else {
      const seedPrompt = `Let's reflect on my goal "${want.title}". It has been stalled for ${stalled_days} days. What is blocking me, and how can we break it down into smaller, actionable steps?`;
      onEngage?.(seedPrompt);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTodo();
    }
  };

  const handleCreateTodo = async (e) => {
    if (e) e.preventDefault();
    if (!newTodoTitle.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/todos`,
        {
          title: newTodoTitle.trim(),
          want_id: want.id,
          being_id: want.being_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTodoTitle('');
      setShowStarterForm(false);
      onCommitted?.();
    } catch (err) {
      console.error('Failed to create todo for stalled goal:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/wants/${want.id}`,
        {
          title: want.title,
          description: want.description,
          being_id: want.being_id,
          target_date: want.target_date,
          archived: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCommitted?.();
    } catch (err) {
      console.error('Failed to archive stalled goal:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 fade-in relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, hsl(4 45% 98%) 0%, hsl(36 40% 97%) 100%)',
        border: '1px solid hsl(4 25% 90% / 0.7)'
      }}
      data-testid="stalled-goal-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-destructive" />
          <span className="text-xs uppercase tracking-[0.18em] text-destructive font-semibold">
            Stalled Goal · {stalled_days} days
          </span>
        </div>
        
        {visibleGoals.length > 1 && (
          <div className="flex items-center gap-1 text-muted-foreground">
            <button 
              type="button"
              onClick={handlePrev}
              className="p-1 hover:bg-background/50 hover:text-foreground rounded transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[10px] tracking-widest font-medium px-1">
              {activeIndex + 1}/{visibleGoals.length}
            </span>
            <button 
              type="button"
              onClick={handleNext}
              className="p-1 hover:bg-background/50 hover:text-foreground rounded transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <h3 className="font-serif text-base sm:text-lg font-medium leading-snug mb-1 text-foreground">
        {want.title}
      </h3>
      <p className="text-xs sm:text-sm text-muted-foreground mb-4">
        {want.description || 'No description provided.'}
      </p>

      {showStarterForm && isStarter ? (
        <div className="space-y-3 mt-3 animate-in fade-in slide-in-from-bottom-1 duration-200">
          <p className="text-xs text-muted-foreground">
            Add a small, immediate next task to get back on track:
          </p>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="e.g. Write for 10 minutes"
              value={newTodoTitle}
              onChange={(e) => setNewTodoTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-xs h-8"
              required
              disabled={submitting}
              autoFocus
            />
            <Button onClick={handleCreateTodo} size="sm" className="h-8 text-xs rounded-lg px-3" disabled={submitting}>
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowStarterForm(false)}
              className="text-[10px] text-muted-foreground hover:text-foreground underline flex-shrink-0"
            >
              Cancel
            </button>
            <span className="text-[10px] text-accent font-semibold text-right ml-2">
              💡 Upgrade to Pro to reflect with your AI coach
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRecommit}
            disabled={submitting}
            className="rounded-full h-8 text-xs px-4 bg-destructive hover:bg-destructive/90 text-white"
            data-testid="recommit-goal-btn"
          >
            {isStarter ? 'Break It Down' : 'Reflect with Coach'}
            <ArrowRight className="w-3 h-3 ml-1.5" />
          </Button>
          <Button
            variant="outline"
            onClick={handleArchive}
            disabled={submitting}
            className="rounded-full h-8 text-xs px-4 text-muted-foreground hover:text-foreground border-border/80"
            data-testid="archive-goal-btn"
          >
            <Archive className="w-3.5 h-3.5 mr-1.5" />
            Archive Goal
          </Button>
        </div>
      )}
    </section>
  );
}
